import React, { useEffect } from 'react';
import { ArrayPath, Control, FieldArray, useFieldArray } from 'react-hook-form';
import { MimeTypeAsString } from '../../utils/fileMimeTypes';
import { FileInput } from './FileInput';
import { FileContent, useImperativeFilePicker } from 'use-file-picker';
import { VirusScanValidator } from '../../utils/useFilePicker/virusScanValidator';
import { FileTypeValidator } from '../../utils/useFilePicker/fileTypeValidator';
import { FilesList } from './FilesList';
import { FormFieldHeader } from '../FormFieldHeader';
import { AttachmentInputFooter } from './AttachmentInputFooter';
import { FilesSizeValidator } from '../../utils/useFilePicker/filesSizeValidator';

type MultiAttachmentControllerProperty = {
  content: string;
  id?: string;
  fileName: string;
  mainController: string;
  size: string;
};

interface PropTypes<E> {
  allowedFormats: MimeTypeAsString[];
  className?: string;
  control: Control<E>;
  controller: MultiAttachmentControllerProperty;
  fieldName?: string;
  disabled?: boolean;
  isRequired?: boolean;
  maxTotalFiles: number;
  maxFileSizeMb: number;
  maxFilesSizeMb?: number;
}

export function MultiAttachmentInputFormField<E>(props: PropTypes<E>) {
  const { fields, append, remove } = useFieldArray({
    control: props.control,
    name: props.controller.mainController as ArrayPath<E>,
    rules: {
      required: props.isRequired,
    },
  });

  const validators = [new FileTypeValidator(), new VirusScanValidator()];
  if (props.maxFilesSizeMb) {
    validators.push(
      new FilesSizeValidator(
        fields.map((i) => i[props.controller.size]),
        props.maxFilesSizeMb,
      ),
    );
  }

  const [
    openFileSelector,
    {
      filesContent,
      errors: filePickerErrors,
      plainFiles,
      loading,
      removeFileByReference,
    },
  ] = useImperativeFilePicker({
    multiple: true,
    readAs: 'DataURL',
    accept: props.allowedFormats,
    maxFileSize: props.maxFileSizeMb,
    readFilesContent: true,
    limitFilesConfig: {
      max: props.maxTotalFiles,
    },
    validators: validators,
  });

  useEffect(() => {
    filesContent.forEach((fileContent: FileContent) => {
      if (
        fields.find((i) => i[props.controller.fileName] === fileContent.name) ==
        null
      ) {
        append({
          [props.controller.fileName]: fileContent.name,
          [props.controller.content]: fileContent.content,
          [props.controller.size]:
            plainFiles.find((v) => v.name === fileContent.name)?.size ?? 0,
        } as FieldArray<E, ArrayPath<E>>);
      }
    });
  }, [filesContent]);

  const handleRemoveField = (index: number) => {
    const plainFileToDelete = plainFiles.find(
      (i) => i.name === fields[index][props.controller.fileName],
    );

    remove(index);

    if (plainFileToDelete != null) {
      removeFileByReference(plainFileToDelete);
    }
  };

  return (
    <div className={props.className}>
      {props.fieldName && (
        <FormFieldHeader
          fieldName={props.fieldName}
          isRequired={props.isRequired}
        />
      )}
      <FileInput
        allowedFormats={props.allowedFormats}
        disabled={fields.length === props.maxTotalFiles}
        fileErrors={filePickerErrors}
        isLoading={loading}
        maxFiles={props.maxTotalFiles}
        maxFileSizeMb={props.maxFileSizeMb}
        maxFilesSizeMb={props.maxFilesSizeMb}
        openFileSelector={openFileSelector}
      />
      <FilesList
        filenameAccessor={props.controller.fileName}
        fields={fields}
        remove={handleRemoveField}
      />
      <AttachmentInputFooter
        allowedFormats={props.allowedFormats}
        maxFileSizeMb={props.maxFileSizeMb}
        maxFilesSizeMb={props.maxFilesSizeMb}
        maxTotalFiles={props.maxTotalFiles}
        errors={filePickerErrors}
      />
    </div>
  );
}
